import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCountdown } from "../helper/Countdown";
import toast from "react-hot-toast";
import brotherProducts from "../data/brother-products.json";
import canonProducts from "../data/canon-products.json";

const DealsOffice = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  const getImage = (item) => {
    if (!item) return "/assets/img/placeholder.png";

    if (Array.isArray(item.imageUrl) && item.imageUrl.length > 0) {
      return item.imageUrl[0];
    }

    if (typeof item.imageUrl === "string" && item.imageUrl.trim()) {
      return item.imageUrl;
    }

    return "/assets/img/placeholder.png";
  };

  useEffect(() => {
    setProducts([...brotherProducts, ...canonProducts]);
  }, []);

  const handleAddToCart = (product) => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");

    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity++;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));
    toast.success(`${product.name} added to cart!`);
  };

  const formatCategory = (str) =>
    str.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const productsWithRandomData = useMemo(() => {
    return products.map((item) => ({
      ...item,
      rating: (Math.random() * 1 + 4).toFixed(1),
      reviews: Math.floor(Math.random() * 500) + 80,
      features: ["High Speed", "Workforce Ready", "Document Heavy", "Eco Smart"]
        .sort(() => 0.5 - Math.random())
        .slice(0, 2),
      formattedCategory: formatCategory(item.category),
    }));
  }, [products]);

  return (
    <section className="office-deals-sec">
      <div className="office-container">
        {/* New Modern Heading */}
        <div className="office-heading">
          <h2>Professional Office Printers</h2>
          <p>
            Built for efficiency, speed, and heavy-duty work — perfect for
            business environments and office workflows.
          </p>
        </div>

        {/* PRODUCT LIST */}
        <div className="office-grid">
          {productsWithRandomData.map((item) => (
            <div key={item.id} className="office-card">
              {/* LEFT IMAGE */}
              <Link
                to={`/product-details/${item.id}`}
                className="office-img-box"
              >
                <img src={getImage(item)} alt={item.name} />
              </Link>

              {/* RIGHT CONTENT */}
              <div className="office-content">
                <p className="office-category">{item.formattedCategory}</p>

                <Link
                  to={`/product-details/${item.id}`}
                  className="office-title"
                >
                  {item.name}
                </Link>

                <p className="office-desc">
                  {item.description
                    ? item.description.split(" ").slice(0, 18).join(" ") + "..."
                    : "Designed for high-volume printing with exceptional durability."}
                </p>

                <div className="office-tags">
                  {item.features.map((tag, i) => (
                    <span key={i} className="office-chip">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* PRICE + BUTTON */}
                <div className="office-bottom">
                  <div className="office-price">${item.price}</div>

                  <button
                    className="office-btn"
                    onClick={() => handleAddToCart(item)}
                  >
                    Add to Cart
                  </button>
                </div>

                <div className="office-rating">
                  ⭐ {item.rating} <span>({item.reviews} reviews)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* === SCOPED CSS === */}
      <style>{`
        .office-deals-sec {
          padding: 70px 0;
          background: #f4f6f9;
        }

        .office-container {
          max-width: 1600px;
          margin: auto;
          padding: 0 20px;
        }

        /* Heading */
        .office-heading {
          text-align: center;
          margin-bottom: 40px;
        }

        .office-heading h2 {
          font-size: 32px;
          font-weight: 800;
          color: #1c1c1c;
        }

        .office-heading p {
          color: #555;
          font-size: 15px;
          max-width: 700px;
          margin: 10px auto 0;
        }

        /* Grid */
        .office-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 28px;
        }

        /* Card Layout (Horizontal) */
        .office-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          display: flex;
          gap: 18px;
          box-shadow: 0px 6px 20px rgba(0,0,0,0.08);
          transition: 0.3s ease;
        }

        .office-card:hover {
          transform: translateY(-4px);
          box-shadow: 0px 14px 28px rgba(0,0,0,0.12);
        }

        /* Image area */
        .office-img-box {
          width: 40%;
          min-width: 140px;
          background: #fff;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 14px;
        }

        .office-img-box img {
          width: 95%;
          transition: 0.3s;
        }

        .office-card:hover img {
          transform: scale(1.04);
        }

        /* TEXT AREA */
        .office-content {
          width: 60%;
          display: flex;
          flex-direction: column;
        }

        .office-category {
          font-size: 12px;
          color: #777;
          margin-bottom: 4px;
          font-weight: 600;
        }

        .office-title {
          font-size: 19px;
          font-weight: 700;
          color: #111;
          text-decoration: none;
          margin-bottom: 8px;
        }

        .office-desc {
          font-size: 14px;
          color: #555;
          margin-bottom: 14px;
          line-height: 1.5;
        }

        /* TAGS */
        .office-tags {
          display: flex;
          gap: 8px;
          margin-bottom: 18px;
        }

        .office-chip {
          background: #e7efff;
          color: #0a53c1;
          padding: 6px 12px;
          font-size: 11px;
          border-radius: 20px;
          white-space: nowrap;
          font-weight: 600;
        }

        /* PRICE + BUTTON */
        .office-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .office-price {
          font-size: 22px;
          font-weight: 800;
          color: #0a53c1;
        }

        .office-btn {
          background: #0a53c1;
          color: white;
          padding: 10px 18px;
          border: none;
          font-size: 13px;
          font-weight: 600;
          border-radius: 10px;
          transition: 0.25s;
        }

        .office-btn:hover {
          background: #083c8f;
          transform: translateY(-2px);
        }

        /* RATING */
        .office-rating {
          border-top: 1px solid #ddd;
          padding-top: 8px;
          font-size: 13px;
          color: #444;
        }

        .office-rating span {
          color: #777;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .office-card {
            flex-direction: column;
          }

          .office-img-box {
            width: 100%;
          }

          .office-content {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};

export default DealsOffice;
